import { BoundingBox } from '@babylonjs/core/Culling/boundingBox';
import { Subject } from 'rxjs';
import { NodeReference } from '../decorators/change-tracker';
import { Kb3dModel } from '../decorators/kb3d-decorator';
import { eAxis } from '../enums';
import { ISketchNode } from '../kb3d-models';
import { Kb3dObject } from '../kb3d-object';
import { IKbVectorLike, KbColor, KbVector } from '../primitives';
import { IBoundingBox } from '../primitives/primitive-utilities';
import { Token } from '../token';
import { MeshNode } from './mesh-node';
import { SketchPath } from './sketch-node-path';

@Kb3dModel({
    token: Token.SketchNode,
})
export class SketchNode extends MeshNode implements ISketchNode {
    public constructor() {
        super();
    }

    _previewVisible: boolean;
    _boundingBoxMin = KbVector.Zero();
    _boundingBoxMax = KbVector.Zero();
    _boundingBoxChanges = new Subject<IBoundingBox>();
    _controlPoints: IKbVectorLike[] = [];
    _lineSegmentStartingPoints: number[] = [];

    // todo remove
    _sketchPoints = new Float32Array();

    calculatedBoundingBox: BoundingBox;
    boundingXOverride?: number;
    boundingYOverride?: number;
    boundingZOverride?: number;
    paths: SketchPath[] = [];
    normal: eAxis;
    /** Whether to generate a face for this path */
    addFace = false;
    closePath = false;
    flipPath = false;

    tracePath = false;
    lineThickness: number;
    pathColor: KbColor;

    /** (Not Implemented) Path to clone. Will cause the paths property to be ignored */
    @NodeReference(SketchNode) clonePath?: string;

    public getChildren(): Kb3dObject[] {
        return super.getChildren().concat(this.paths);
    }

    public getChildArrayForType(token: Token): Kb3dObject[] | undefined {
        if (token.isSketchPath()) {
            return this.paths;
        } else {
            return super.getChildArrayForType(token);
        }
    }

    public getBoundingBox() {
        const min = new KbVector(
            this.boundingXOverride == null ? this._boundingBoxMin.x : -this.boundingXOverride / 2,
            this.boundingYOverride == null ? this._boundingBoxMin.y : -this.boundingYOverride / 2,
            this.boundingZOverride == null ? this._boundingBoxMin.z : -this.boundingZOverride / 2
        );
        const max = new KbVector(
            this.boundingXOverride == null ? this._boundingBoxMax.x : this.boundingXOverride / 2,
            this.boundingYOverride == null ? this._boundingBoxMax.y : this.boundingYOverride / 2,
            this.boundingZOverride == null ? this._boundingBoxMax.z : this.boundingZOverride / 2
        );
        const centerBB = this._boundingBoxMin.add(this._boundingBoxMax).scale(0.5);
        const center = new KbVector(
            this.boundingXOverride == null ? centerBB.x : 0,
            this.boundingYOverride == null ? centerBB.y : 0,
            this.boundingZOverride == null ? centerBB.z : 0
        );
        return {
            min,
            max,
            center,
        };
    }

    public getSketchLength() {
        let sketchLength = 0;
        this.paths.forEach(path => {
            let lastPosition: IKbVectorLike | undefined = undefined;
            path._points.forEach(point => {
                if (!lastPosition) {
                    lastPosition = point;
                } else {
                    const currentPos = point;
                    const distance = Math.sqrt(
                        Math.pow(lastPosition.x - currentPos.x, 2) +
                            Math.pow(lastPosition.y - currentPos.y, 2) +
                            Math.pow(lastPosition.z - currentPos.z, 2)
                    );
                    sketchLength += distance;
                    lastPosition = currentPos;
                }
            });
        });
        return sketchLength;
    }
}
